/**
 * EducationSection — education level dropdown + "Other" text field.
 *
 * Flutter parity: `education_form.dart`. Selecting an entry sets
 * `patientEducation.educationCode` (e.g. "BD", "OTH") + the display label
 * mirror. If `educationDisplay === "Other"`, the additional
 * `educationInformation` free-text field renders so the user can
 * describe their level.
 *
 * The whole `patientEducation` sub-object can also be cleared back to
 * null when the dropdown is reset.
 */

import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { TextField, Select } from '@scripsteam/scrips-signal-ds';
import { useEducationLevels } from '@/shared/services/lookups';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type { PersonalDetailsFormValues } from './personal-details-schema';

export function EducationSection() {
  const { control, setValue } = useFormContext<PersonalDetailsFormValues>();
  const educationDisplay = useWatch({
    control,
    name: 'patientEducation.educationDisplay',
  });
  const educationInformation = useWatch({
    control,
    name: 'patientEducation.educationInformation',
  });
  const educationCode = useWatch({
    control,
    name: 'patientEducation.educationCode',
  });

  const levelsQ = useEducationLevels();

  const isOther = (educationDisplay ?? '').toLowerCase() === 'other';

  return (
    <div className="grid gap-6">
      <div>
        <FieldLabel>Education level</FieldLabel>
        <Controller
          control={control}
          name="patientEducation.educationCode"
          render={({ field }) => (
            // DS Select's `options` array carries {value,label} pairs, so
            // it always renders the selected label in the trigger, even
            // before the async education options mount.
            <Select
              className="mt-1"
              value={field.value ?? ''}
              onChange={(v) => {
                if (!v) {
                  // Reset the entire sub-object to null so the wire body
                  // doesn't ship partial education state.
                  setValue('patientEducation', null, { shouldDirty: true });
                  return;
                }
                const entry = levelsQ.data?.find((e) => e.code === v);
                setValue(
                  'patientEducation',
                  {
                    id: null,
                    patientId: null,
                    educationCode: entry?.code ?? v,
                    educationDisplay: entry?.display ?? null,
                    educationSystem: entry?.system ?? null,
                    educationInformation: null,
                  },
                  { shouldDirty: true },
                );
                field.onChange(v);
              }}
              placeholder="Select education level"
              options={(levelsQ.data ?? []).map((e) => ({
                value: e.code,
                label: e.display,
              }))}
            />
          )}
        />
      </div>

      {isOther ? (
        <div>
          <FieldLabel>Education information</FieldLabel>
          <TextField
            value={educationInformation ?? ''}
            onChange={(e) =>
              setValue(
                'patientEducation.educationInformation',
                e.target.value || null,
                { shouldDirty: true },
              )
            }
            placeholder="Enter info"
            className="mt-1" size="compact"
          />
        </div>
      ) : null}

      {/* Tracked for the save round-trip; not rendered. */}
      <input type="hidden" value={educationCode ?? ''} readOnly hidden />
    </div>
  );
}
